import { createStoreWithProducer } from '@xstate/store';
import { FragmentOf } from 'gql.tada';
import { produce } from 'immer';
import sortedIndexBy from 'lodash/sortedIndexBy';
import React from 'react';

import { AudioPlayerHandle } from '@eluve/audio-player';

import { segmentFragment } from './operations';

type DefaultContext = {
  currentTimestamp: number;
  selectedUtteranceIds: string[];
  tenantId: string;
  segmentId: string;
  isReadonly: boolean;
  hoverFragmentId: string;
  activeUtteranceId: string;
  activeFragmentId: string;
  focusedUtteranceId: string;
  activeFragment?: {
    id: string;
    startAt: number;
    endAt: number;
    utteranceId: string;
  };
  mappedFragments: {
    id: string;
    utteranceId: string;
    startAt: number;
    endAt: number;
  }[];
  mappedFragmentIndex: number;
};

const defaultContext: DefaultContext = {
  currentTimestamp: 0,
  selectedUtteranceIds: [],
  tenantId: '',
  segmentId: '',
  isReadonly: false,
  hoverFragmentId: '',
  activeUtteranceId: '',
  activeFragmentId: '',
  focusedUtteranceId: '',
  mappedFragments: [],
  mappedFragmentIndex: -1,
};

const findIndexForFragment = (
  fragments: Array<{
    startAt: number;
    endAt: number;
    id: string;
    utteranceId: string;
  }>,
  timestamp: number,
) => {
  const index = sortedIndexBy(
    fragments,
    {
      startAt: timestamp,
      endAt: 0,
      id: '',
      utteranceId: '',
    },
    (obj) => obj.startAt,
  );

  const fragment = fragments[index - 1];
  if (
    fragment &&
    fragment.startAt <= timestamp &&
    fragment.endAt >= timestamp
  ) {
    return index - 1;
  }

  return -1;
};

export const createSegmentAudioContextStore = (args: {
  tenantId: string;
  segmentId: string;
  isReadonly: boolean;
}) => {
  const { tenantId, segmentId, isReadonly } = args;

  return createStoreWithProducer(
    produce,
    { ...defaultContext, tenantId, segmentId, isReadonly },
    {
      updateTimestamp: (ctx, evt: { timestamp: number }) => {
        ctx.currentTimestamp = evt.timestamp;

        // Shortcircuit if the active fragment is already set
        // and the timestamp is within the fragment range
        if (
          ctx.activeFragment &&
          ctx.activeFragment.startAt >= evt.timestamp &&
          ctx.activeFragment.endAt <= evt.timestamp
        ) {
          return;
        }
        // Optimisation one: check if the next fragment is the correct one
        if (ctx.mappedFragmentIndex >= 0) {
          const nextFragment = ctx.mappedFragments[ctx.mappedFragmentIndex + 1];
          if (
            nextFragment &&
            nextFragment.startAt <= evt.timestamp &&
            nextFragment.endAt >= evt.timestamp
          ) {
            ctx.mappedFragmentIndex += 1;
            ctx.activeFragment = nextFragment;
            ctx.activeFragmentId = nextFragment.id;
            ctx.activeUtteranceId = nextFragment.utteranceId;
            return;
          }
        }
        // Optimisation two: run search using binary search instead of find
        const foundFragmentIndex = findIndexForFragment(
          ctx.mappedFragments,
          evt.timestamp,
        );
        const foundFragment = ctx.mappedFragments[foundFragmentIndex];
        if (foundFragment) {
          ctx.mappedFragmentIndex = foundFragmentIndex;
          ctx.activeFragment = foundFragment;
          ctx.activeFragmentId = foundFragment.id;
          ctx.activeUtteranceId = foundFragment.utteranceId;
        }
      },
      updateSegment(ctx, evt: { segment: FragmentOf<typeof segmentFragment> }) {
        const mappedFragments = evt.segment.utterances
          .flatMap((u) =>
            u.fragments.map((f) => ({
              id: f.id,
              utteranceId: u.id,
              startAt: f.startAt ?? 0,
              endAt: f.endAt ?? 0,
            })),
          )
          .sort((a, b) => a.startAt - b.startAt);

        if (mappedFragments.length === 0) {
          return;
        }
        ctx.mappedFragments = mappedFragments;
        // Reset active states when segment updates
        ctx.activeFragment = undefined;
        ctx.activeFragmentId = '';
        ctx.activeUtteranceId = '';
        ctx.selectedUtteranceIds = [];
        ctx.hoverFragmentId = '';
      },
      updateHoverFragmentId: (ctx, evt: { fragmentId: string }) => {
        ctx.hoverFragmentId = evt.fragmentId;
      },
      clearHoverFragmentId: (ctx) => {
        ctx.hoverFragmentId = '';
      },
      selectUtteranceId: (ctx, evt: { utteranceId: string }) => {
        ctx.selectedUtteranceIds.push(evt.utteranceId);
      },
      deselectUtteranceId: (ctx, evt: { utteranceId: string }) => {
        const idx = ctx.selectedUtteranceIds.indexOf(evt.utteranceId);
        if (idx !== -1) {
          ctx.selectedUtteranceIds.splice(idx, 1);
        }
      },
      updateActiveUtteranceId: (ctx, evt: { utteranceId: string }) => {
        ctx.activeUtteranceId = evt.utteranceId;
      },
      clearActiveUtteranceId: (ctx) => {
        ctx.activeUtteranceId = '';
      },
      clearSelectedUtteranceIds: (ctx) => {
        ctx.selectedUtteranceIds = [];
      },
      setFocusedUtteranceId: (ctx, evt: { utteranceId: string }) => {
        ctx.focusedUtteranceId = evt.utteranceId;
      },
      clearFocusedUtteranceId: (ctx) => {
        ctx.focusedUtteranceId = '';
      },
    },
  );
};

type SegmentStore = ReturnType<typeof createSegmentAudioContextStore>;

export type SegmentAudioContextValue = {
  store: SegmentStore;
  audioPlayerHandle: React.RefObject<AudioPlayerHandle>;
};

export const SegmentAudioContext =
  React.createContext<SegmentAudioContextValue | null>(null);

export const useSegmentAudioContext = () => {
  const context = React.useContext(SegmentAudioContext);
  if (context === null) {
    throw new Error(
      'useSegmentAudioContext must be used within a SegmentAudioContextProvider',
    );
  }
  return context;
};
